import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { BookOpen, ChevronDown, ChevronUp, Play, DollarSign, Receipt, Wallet, FileText, Shield, Users, Store } from 'lucide-react';

const Help = () => {
  const { user } = useAuth();
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const sections = [
    {
      id: 'intro',
      title: '🎯 Introducción al Sistema',
      icon: <BookOpen size={24} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Bienvenido al <strong>Sistema de Control de Flujo de Caja</strong> para las tiendas Siempre.
            Este sistema te permite administrar el flujo de efectivo, registrar ventas, gastos y generar reportes detallados.
          </p>
          <div className="bg-cyan-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Características Principales:</h4>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Registro de ventas por vendedor y forma de pago</li>
              <li>Control de gastos con autorización</li>
              <li>Gestión de fondo de sencillo por turno</li>
              <li>Reportes detallados de turnos</li>
              <li>Auditoría completa de todas las acciones</li>
              <li>Notificaciones en tiempo real</li>
            </ul>
          </div>
        </div>
      ),
      roles: ['admin', 'manager', 'supervisor', 'store_manager', 'cashier']
    },
    {
      id: 'login',
      title: '🔐 Iniciar Sesión',
      icon: <Play size={24} />,
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Pasos para iniciar sesión:</h4>
          <ol className="list-decimal list-inside space-y-3 text-gray-700">
            <li>Abre la aplicación en tu navegador</li>
            <li>Ingresa tu <strong>usuario</strong> (proporcionado por administración)</li>
            <li>Ingresa tu <strong>contraseña</strong></li>
            <li>Haz clic en "Iniciar Sesión"</li>
          </ol>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              ⚠️ <strong>Importante:</strong> Si es tu primer acceso, contacta al administrador para obtener tus credenciales.
              Para tiendas, se comparte un usuario por tienda.
            </p>
          </div>
        </div>
      ),
      roles: ['admin', 'manager', 'supervisor', 'store_manager', 'cashier']
    },
    {
      id: 'sales',
      title: '💰 Registro de Ventas',
      icon: <DollarSign size={24} />,
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Cómo registrar una venta:</h4>
          <ol className="list-decimal list-inside space-y-3 text-gray-700">
            <li>Ve a la sección <strong>"Ventas"</strong> en el menú</li>
            <li>Haz clic en el botón <strong>"Nueva Venta"</strong></li>
            <li>Completa el formulario:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li><strong>Nombre del Vendedor:</strong> Tu nombre o del cajero</li>
                <li><strong>Forma de Pago:</strong> POS/Tarjeta, Efectivo o Recargas</li>
                <li><strong>Monto:</strong> Cantidad en Quetzales (Q)</li>
              </ul>
            </li>
            <li>Haz clic en <strong>"Registrar Venta"</strong></li>
          </ol>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              💡 <strong>Consejo:</strong> El sistema actualiza automáticamente el saldo de la tienda al registrar ventas.
              Puedes filtrar ventas por fecha usando los filtros en la parte superior.
            </p>
          </div>
        </div>
      ),
      roles: ['admin', 'manager', 'supervisor', 'store_manager', 'cashier']
    },
    {
      id: 'expenses',
      title: '🧾 Registro de Gastos',
      icon: <Receipt size={24} />,
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Cómo registrar un gasto:</h4>
          <ol className="list-decimal list-inside space-y-3 text-gray-700">
            <li>Ve a la sección <strong>"Gastos"</strong> en el menú</li>
            <li>Haz clic en el botón <strong>"Nuevo Gasto"</strong></li>
            <li>Completa el formulario:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li><strong>Categoría:</strong> Selecciona la categoría (Proveedor, Servicios, etc.)</li>
                <li><strong>Descripción:</strong> Detalla qué se compró o pagó</li>
                <li><strong>Monto:</strong> Cantidad gastada en Quetzales (Q)</li>
              </ul>
            </li>
            <li>Haz clic en <strong>"Registrar Gasto"</strong></li>
          </ol>
          
          <h4 className="font-semibold text-gray-900 mt-6">Gestionar Categorías (Solo Encargados):</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Haz clic en el botón <strong>"Categorías"</strong></li>
            <li>Para agregar: escribe el nombre y haz clic en "Agregar"</li>
            <li>Para eliminar: haz clic en el icono de basura junto a la categoría</li>
          </ol>

          <h4 className="font-semibold text-gray-900 mt-6">Editar un Gasto (Solo Encargados):</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Encuentra el gasto en la lista y haz clic en <strong>"Editar"</strong></li>
            <li>Ingresa el nuevo monto</li>
            <li>Explica la razón de la edición</li>
            <li>Ingresa el <strong>código de autorización</strong> (lo genera el encargado)</li>
            <li>Confirma los cambios</li>
          </ol>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              🚨 <strong>Importante:</strong> Una vez registrado un gasto, solo el encargado puede editarlo con un código de autorización.
              Esto mantiene la seguridad y auditoría del sistema.
            </p>
          </div>
        </div>
      ),
      roles: ['admin', 'manager', 'supervisor', 'store_manager', 'cashier']
    },
    {
      id: 'petty-cash',
      title: '💵 Fondo de Sencillo',
      icon: <Wallet size={24} />,
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Contar el Fondo de Sencillo:</h4>
          <ol className="list-decimal list-inside space-y-3 text-gray-700">
            <li>Ve a la sección <strong>"Fondo Sencillo"</strong></li>
            <li>Haz clic en <strong>"Contar Fondo"</strong></li>
            <li>Selecciona el turno actual:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li><strong>Turno 1:</strong> 5:00am - 1:00pm</li>
                <li><strong>Turno 2:</strong> 1:00pm - 9:00pm</li>
                <li><strong>Turno 3:</strong> 9:00pm - 5:00am</li>
              </ul>
            </li>
            <li>Ingresa el monto contado</li>
            <li>Haz clic en <strong>"Registrar Conteo"</strong></li>
          </ol>

          <h4 className="font-semibold text-gray-900 mt-6">Usar el Fondo de Sencillo:</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Haz clic en <strong>"Usar Fondo"</strong></li>
            <li>Ingresa el monto a usar</li>
            <li>Explica la razón (ej: "Pago a proveedor por falta de efectivo")</li>
            <li>Ingresa el <strong>código de autorización</strong> del encargado</li>
            <li>Confirma la operación</li>
          </ol>

          <h4 className="font-semibold text-gray-900 mt-6">Generar Código de Autorización (Solo Encargados):</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Haz clic en <strong>"Generar Código"</strong></li>
            <li>Ingresa el número de turno</li>
            <li>El sistema generará un código de 6 caracteres (3 letras + 3 números)</li>
            <li>Comparte este código con el cajero que lo necesite</li>
            <li>El código es válido por 8 horas</li>
          </ol>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-purple-800 text-sm">
              🔐 <strong>Seguridad:</strong> El código de autorización cambia en cada turno y solo el encargado puede verlo.
              Esto previene que los cajeros usen el fondo sin autorización.
            </p>
          </div>
        </div>
      ),
      roles: ['admin', 'manager', 'supervisor', 'store_manager', 'cashier']
    },
    {
      id: 'reports',
      title: '📊 Reportes y Dashboard',
      icon: <FileText size={24} />,
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Ver el Dashboard:</h4>
          <p className="text-gray-700">
            El Dashboard muestra un resumen en tiempo real de cada tienda:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
            <li>Saldo disponible actual</li>
            <li>Fondo de sencillo</li>
            <li>Ventas del día</li>
            <li>Gastos del día</li>
            <li>Balance neto del día</li>
          </ul>

          <h4 className="font-semibold text-gray-900 mt-6">Generar Reporte de Turno (Solo Encargados):</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Ve a la sección <strong>"Reportes"</strong></li>
            <li>Haz clic en <strong>"Generar Reporte"</strong></li>
            <li>Selecciona la fecha</li>
            <li>Selecciona el número de turno</li>
            <li>Haz clic en <strong>"Generar Reporte"</strong></li>
            <li>El reporte mostrará:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Total de ventas del turno</li>
                <li>Total de gastos del turno</li>
                <li>Balance neto</li>
                <li>Detalle de cada venta y gasto</li>
              </ul>
            </li>
            <li>Puedes imprimir el reporte usando el botón "Imprimir"</li>
          </ol>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-sm">
              📈 <strong>Consejo:</strong> Genera reportes al final de cada turno solo cuando haya discrepancias.
              No es obligatorio generar un reporte cada turno.
            </p>
          </div>
        </div>
      ),
      roles: ['admin', 'manager', 'supervisor', 'store_manager', 'cashier']
    },
    {
      id: 'audit',
      title: '🔍 Auditoría',
      icon: <Shield size={24} />,
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Ver el Historial de Auditoría:</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Ve a la sección <strong>"Auditoría"</strong></li>
            <li>Verás una lista completa de todas las acciones realizadas</li>
            <li>Cada registro muestra:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Fecha y hora exacta</li>
                <li>Usuario que realizó la acción</li>
                <li>Tipo de acción</li>
                <li>Detalles de lo que se hizo</li>
              </ul>
            </li>
          </ol>

          <h4 className="font-semibold text-gray-900 mt-6">Filtrar Auditoría:</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
            <li>Usa el campo de búsqueda para encontrar acciones específicas</li>
            <li>Filtra por rango de fechas (inicio y fin)</li>
            <li>Haz clic en "Limpiar Filtros" para ver todo</li>
          </ul>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-800 text-sm">
              📝 <strong>Nota:</strong> La auditoría es inmutable, ninguna acción puede ser borrada.
              Esto garantiza transparencia total en todas las operaciones.
            </p>
          </div>
        </div>
      ),
      roles: ['admin', 'manager', 'supervisor', 'store_manager']
    },
    {
      id: 'admin',
      title: '👥 Administración (Solo Admin)',
      icon: <Users size={24} />,
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Crear Usuarios:</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Ve a la sección <strong>"Usuarios"</strong></li>
            <li>Haz clic en <strong>"Nuevo Usuario"</strong></li>
            <li>Completa el formulario:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Nombre completo</li>
                <li>Usuario</li>
                <li>Email</li>
                <li>Contraseña</li>
                <li>Rol (Admin, Gerencia, Supervisor, Encargado, Cajero)</li>
                <li>Tienda (si aplica)</li>
              </ul>
            </li>
            <li>Haz clic en <strong>"Crear Usuario"</strong></li>
          </ol>

          <h4 className="font-semibold text-gray-900 mt-6">Crear Tiendas:</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Ve a la sección <strong>"Tiendas"</strong></li>
            <li>Haz clic en <strong>"Nueva Tienda"</strong></li>
            <li>Completa el formulario:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Nombre de la tienda</li>
                <li>Moneda (Q - Quetzal, $ - Dólar, etc.)</li>
                <li>Flujo de caja inicial</li>
                <li>Fondo de sencillo inicial</li>
              </ul>
            </li>
            <li>Haz clic en <strong>"Crear Tienda"</strong></li>
          </ol>
        </div>
      ),
      roles: ['admin']
    },
    {
      id: 'tips',
      title: '💡 Consejos y Buenas Prácticas',
      icon: <Play size={24} />,
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-3">Tips para Cajeros:</h4>
            <ul className="list-disc list-inside space-y-2 text-blue-800">
              <li>Registra las ventas inmediatamente después de cada transacción</li>
              <li>Verifica el monto antes de confirmar</li>
              <li>Cuenta el fondo de sencillo al inicio de tu turno</li>
              <li>Si necesitas editar algo, solicita al encargado el código de autorización</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-3">Tips para Encargados:</h4>
            <ul className="list-disc list-inside space-y-2 text-purple-800">
              <li>Genera un nuevo código de autorización al inicio de cada turno</li>
              <li>Comparte el código solo con personal autorizado</li>
              <li>Revisa el Dashboard al menos 2 veces por turno</li>
              <li>Genera reportes cuando haya discrepancias</li>
              <li>Crea categorías de gastos específicas para tu tienda</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-3">Tips para Administradores:</h4>
            <ul className="list-disc list-inside space-y-2 text-green-800">
              <li>Revisa las notificaciones diariamente</li>
              <li>Consulta la auditoría semanalmente</li>
              <li>Verifica que cada tienda tenga su moneda correcta</li>
              <li>Capacita a los nuevos usuarios mostrándoles esta guía</li>
            </ul>
          </div>
        </div>
      ),
      roles: ['admin', 'manager', 'supervisor', 'store_manager', 'cashier']
    }
  ];

  const userSections = sections.filter(section => section.roles.includes(user?.role));

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-3" data-testid="help-title">
          📚 Centro de Ayuda
        </h1>
        <p className="text-gray-600 text-lg">
          Guía completa para usar el Sistema de Control de Flujo de Caja
        </p>
      </div>

      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-2xl font-bold mb-2">¡Bienvenido!</h2>
        <p className="text-blue-50">
          Encuentra aquí todas las instrucciones que necesitas para usar el sistema de manera efectiva.
          Haz clic en cualquier sección para expandirla y ver los detalles.
        </p>
      </div>

      <div className="space-y-4">
        {userSections.map((section) => (
          <div key={section.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="text-blue-600">{section.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
              </div>
              {openSection === section.id ? (
                <ChevronUp className="text-gray-400" size={24} />
              ) : (
                <ChevronDown className="text-gray-400" size={24} />
              )}
            </button>
            
            {openSection === section.id && (
              <div className="p-6 pt-0 border-t">
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <h3 className="text-xl font-semibold text-yellow-900 mb-2">¿Necesitas más ayuda?</h3>
        <p className="text-yellow-800">
          Si tienes preguntas que no están cubiertas en esta guía, contacta al administrador del sistema
          o al soporte técnico de tu empresa.
        </p>
      </div>
    </div>
  );
};

export default Help;
